import * as React from "react";

import { Button } from "./Button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from "./Dialog";
import { FormField } from "./FormField";
import { Input } from "./Input";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  tone?: "danger" | "primary";
  /**
   * Runs on Confirm. While a returned promise is pending the button shows a spinner and the dialog
   * can't be dismissed; it closes when the promise resolves and stays open if it rejects (report
   * the error from `onConfirm`, e.g. with a toast).
   */
  onConfirm: () => void | Promise<void>;
  /** When set, Confirm stays disabled until the user types this text exactly. */
  confirmText?: string;
}

interface ConfirmPanelProps
  extends Omit<ConfirmDialogProps, "open" | "onOpenChange" | "tone" | "onConfirm"> {
  tone: "danger" | "primary";
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// Rendered inside DialogContent, which unmounts on close, so the typed text resets every time.
const ConfirmPanel: React.FC<ConfirmPanelProps> = ({
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone,
  confirmText,
  pending,
  onConfirm,
  onCancel,
}) => {
  const [typed, setTyped] = React.useState("");
  const canConfirm = !confirmText || typed === confirmText;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (canConfirm && !pending) {
      onConfirm();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
      <DialogHeader title={title} description={description} />
      {confirmText && (
        <DialogBody>
          <FormField
            label={
              <>
                Type{" "}
                <code className="rounded bg-surface-muted px-1 py-0.5 font-mono text-xs text-foreground">
                  {confirmText}
                </code>{" "}
                to confirm
              </>
            }
          >
            <Input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              disabled={pending}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </FormField>
        </DialogBody>
      )}
      <DialogFooter>
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          {cancelLabel}
        </Button>
        <Button
          type="submit"
          variant={tone === "danger" ? "danger" : "primary"}
          loading={pending}
          disabled={!canConfirm}
        >
          {confirmLabel}
        </Button>
      </DialogFooter>
    </form>
  );
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  tone = "danger",
  onConfirm,
  ...rest
}) => {
  const [pending, setPending] = React.useState(false);

  const handleOpenChange = (next: boolean) => {
    if (!pending) {
      onOpenChange(next);
    }
  };

  const handleConfirm = async () => {
    setPending(true);
    try {
      await onConfirm();
    } catch {
      // The caller reports the failure; keeping the dialog open lets the user retry or cancel.
      setPending(false);
      return;
    }
    setPending(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-ui="confirm-dialog" role="alertdialog" size="sm" hideClose>
        <ConfirmPanel
          {...rest}
          tone={tone}
          pending={pending}
          onConfirm={handleConfirm}
          onCancel={() => handleOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
};
